import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut, Trash2, Pencil, X, Image, Eye, EyeOff } from "lucide-react";
import { styleCategories } from "./data/collections";

const API = process.env.REACT_APP_BACKEND_URL;

const COLLECTIONS = [
  { label: "Real Jari", collection: "Real Jari", category: "Sarees" },
  { label: "Gota Patti Sarees", collection: "Gota Patti Sarees", category: "Sarees" },
  { label: "Bandhej", collection: "Bandhej", category: "Sarees" },
  { label: "Lehariya", collection: "Lehariya", category: "Sarees" },
  { label: "Jardoji Work", collection: "Jardoji Work", category: "Sarees" },
  { label: "Sikhiya Work", collection: "Sikhiya Work", category: "Sarees" },
  { label: "Binni Saree", collection: "Binni Saree", category: "Sarees" },
  { label: "Diamond Chiffon Printed Saree", collection: "Diamond Chiffon Printed Saree", category: "Sarees" },
  { label: "Dola Silk Printed Saree", collection: "Dola Silk Printed Saree", category: "Sarees" },
  { label: "Fancy Saree", collection: "Fancy Saree", category: "Sarees" },
  { label: "Banarasi Saree", collection: "Banarasi Saree", category: "Sarees" },
  { label: "Boutique Saree", collection: "Boutique Saree", category: "Sarees" },
  { label: "Hand Work", collection: "Hand Work", category: "Sarees" },
  { label: "Aari Tori Work", collection: "Aari Tori Work", category: "Sarees" },
  { label: "Organza Saree", collection: "Organza Saree", category: "Sarees" },
  { label: "Under ₹2000", collection: "Under ₹2000", category: "Sarees" },
  { label: "Lehenga / Chunni", collection: "", category: "Lehengas" },
  { label: "Suit Sets", collection: "Suit Sets", category: "Suits" },
  { label: "Cord Sets", collection: "Cord Sets", category: "Suits" },
];

const authHeaders = (token, json = true) => ({ Authorization: `Bearer ${token}`, ...(json ? { "Content-Type": "application/json" } : {}) });

function LoginForm({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Login failed");
      onLogin(data.token);
    } catch (err) { setError(err.message); }
    setBusy(false);
  };
  return (
    <form className="admin-card admin-login" onSubmit={submit}>
      <img className="admin-logo" src="/assets/logo-mark.png" alt="Shri Balaji Sarees" />
      <p className="eyebrow">Admin panel</p>
      <h2>Manage your <em>collection.</em></h2>
      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin password" className="admin-input" data-testid="admin-password-input" autoComplete="current-password" />
      {error && <p className="admin-error" data-testid="admin-login-error">{error}</p>}
      <button className="button button-primary admin-btn" type="submit" disabled={busy} data-testid="admin-login-button">{busy ? "Checking…" : "Login"}</button>
      <Link to="/" className="text-link admin-back"><ArrowLeft size={14} /> Back to website</Link>
    </form>
  );
}

function ProductForm({ token, editing, onDone, onCancel }) {
  const empty = { collectionIdx: 0, name: "", price: "", fabric: "", colour: "", description: "", photos: [], premium: "", featured: false, hidden: false, order: "" };
  const [form, setForm] = useState(empty);
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editing) {
      const idx = Math.max(0, COLLECTIONS.findIndex((c) => c.collection === (editing.collection || "") && c.category === editing.category));
      setForm({ collectionIdx: idx, name: editing.name, price: editing.price || "", fabric: editing.fabric || "", colour: editing.colour || "", description: editing.description || "", photos: [editing.mainImage, ...(editing.details || [])].filter(Boolean), premium: editing.premium || "", featured: !!editing.featured, hidden: !!editing.hidden, order: editing.order ?? "" });
      setFiles([]);
    } else { setForm(empty); setFiles([]); }
    setStatus("");
  }, [editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pick = (e) => {
    const chosen = Array.from(e.target.files || []).slice(0, 12 - form.photos.length);
    setFiles((prev) => [...prev, ...chosen].slice(0, 12));
    e.target.value = "";
  };
  const removePhoto = (i) => {
    const existing = form.photos.length;
    if (i < existing) set("photos", form.photos.filter((_, x) => x !== i));
    else setFiles((prev) => prev.filter((_, x) => x !== i - existing));
  };

  const publish = async () => {
    if (!form.name.trim()) { setStatus("Product name likhna zaroori hai"); return; }
    if (form.price && (isNaN(Number(form.price)) || Number(form.price) <= 0)) { setStatus("Please enter a valid price"); return; }
    if (form.photos.length + files.length === 0) { setStatus("Kam se kam 1 photo zaroori hai"); return; }
    setBusy(true); setStatus("Photos upload ho rahi hain…");
    try {
      let uploaded = [];
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        const up = await fetch(`${API}/api/admin/upload`, { method: "POST", headers: authHeaders(token, false), body: fd });
        const upData = await up.json();
        if (!up.ok) throw new Error(upData.detail || "Upload failed");
        uploaded = upData.urls;
      }
      setStatus(editing ? "Updating…" : "Publishing…");
      const col = COLLECTIONS[form.collectionIdx];
      const body = { name: form.name.trim(), price: form.price ? Number(form.price) : null, fabric: form.fabric, colour: form.colour, description: form.description, collection: col.collection || null, category: col.category, photos: [...form.photos, ...uploaded], premium: form.premium ? Number(form.premium) : null, featured: form.featured, hidden: form.hidden, order: form.order !== "" ? Number(form.order) : null };
      const res = await fetch(editing ? `${API}/api/admin/products/${editing.id}` : `${API}/api/admin/products`, { method: editing ? "PUT" : "POST", headers: authHeaders(token), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      setStatus(editing ? "Product update ho gaya ✓" : "Product live ho gaya ✓");
      setForm(empty); setFiles([]);
      onDone();
    } catch (err) { setStatus(err.message); }
    setBusy(false);
  };

  return (
    <div className="admin-card">
      <h3>{editing ? "Edit product" : "Add new product"}</h3>
      <label className="admin-label">Collection
        <select className="admin-input" value={form.collectionIdx} onChange={(e) => set("collectionIdx", Number(e.target.value))} data-testid="admin-collection-select">
          {COLLECTIONS.map((c, i) => <option key={c.label} value={i}>{c.label}</option>)}
        </select>
      </label>
      <label className="admin-label">Product name
        <input className="admin-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Red Banarasi Saree" data-testid="admin-name-input" />
      </label>
      <label className="admin-label">Price (MRP ₹) — 10% OFF apne aap lagega
        <input className="admin-input" type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="e.g. 12900" data-testid="admin-price-input" />
      </label>
      {form.price > 0 && <p className="admin-hint">Website par dikhega: <s>₹{Number(form.price).toLocaleString("en-IN")}</s> → ₹{Math.round(Number(form.price) * 0.9).toLocaleString("en-IN")} (10% OFF)</p>}
      <label className="admin-label">Premium saree? (homepage ke 5 premium slots mein dikhega)
        <select className="admin-input" value={form.premium} onChange={(e) => set("premium", e.target.value)} data-testid="admin-premium-select">
          <option value="">Nahi</option>
          {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>Haan — Premium position #{n}</option>)}
        </select>
      </label>
      <div className="admin-checks">
        <label className="admin-check" data-testid="admin-featured-check"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured (card par Featured tag dikhega)</label>
        <label className="admin-check" data-testid="admin-hidden-check"><input type="checkbox" checked={form.hidden} onChange={(e) => set("hidden", e.target.checked)} /> Hide karein (website par nahi dikhega)</label>
      </div>
      <label className="admin-label">Display order (optional — chhota number pehle dikhega)
        <input className="admin-input" type="number" value={form.order} onChange={(e) => set("order", e.target.value)} placeholder="e.g. 1" data-testid="admin-order-input" />
      </label>
      <label className="admin-label">Fabric / details
        <input className="admin-input" value={form.fabric} onChange={(e) => set("fabric", e.target.value)} placeholder="e.g. Pure Georgette" data-testid="admin-fabric-input" />
      </label>
      <label className="admin-label">Colour (optional)
        <input className="admin-input" value={form.colour} onChange={(e) => set("colour", e.target.value)} placeholder="e.g. Red" data-testid="admin-colour-input" />
      </label>
      <label className="admin-label">Description (optional)
        <textarea className="admin-input" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Product ke baare mein short detail" data-testid="admin-description-input" />
      </label>
      <label className="admin-label">Photos ({form.photos.length + files.length}/12) — pehli photo main photo hogi
        <input className="admin-input" type="file" accept="image/*" multiple onChange={pick} data-testid="admin-photo-input" />
      </label>
      <div className="admin-previews">
        {form.photos.map((u, i) => <span key={`e${i}`} className="admin-thumb"><img src={u} alt="" /><button type="button" onClick={() => removePhoto(i)} aria-label="Remove photo"><X size={13} /></button></span>)}
        {files.map((f, i) => <span key={`n${i}`} className="admin-thumb"><img src={URL.createObjectURL(f)} alt="" /><button type="button" onClick={() => removePhoto(form.photos.length + i)} aria-label="Remove photo"><X size={13} /></button></span>)}
      </div>
      {status && <p className="admin-status" data-testid="admin-status">{status}</p>}
      <div className="admin-actions">
        <button className="button button-primary admin-btn" onClick={publish} disabled={busy} data-testid="admin-publish-button">{busy ? "Please wait…" : editing ? "Update product" : "Publish product"}</button>
        {editing && <button className="button button-ghost admin-btn" onClick={onCancel} data-testid="admin-cancel-edit-button">Cancel</button>}
      </div>
    </div>
  );
}

function ProductList({ token, onEdit, refreshKey, preset }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fColl, setFColl] = useState("all");
  const [fPrice, setFPrice] = useState("all");
  const [fFlag, setFFlag] = useState("all");
  useEffect(() => { if (preset) { setFFlag(preset.flag || "all"); setFColl(preset.coll || "all"); setFPrice("all"); } }, [preset]);
  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/products`, { headers: authHeaders(token, false) });
      setItems(await res.json());
    } catch { setItems([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [refreshKey]);
  const del = async (p) => {
    if (!window.confirm(`"${p.name}" delete karein? Website se turant hat jayega.`)) return;
    await fetch(`${API}/api/admin/products/${p.id}`, { method: "DELETE", headers: authHeaders(token, false) });
    load();
  };
  const toggleHide = async (p) => {
    await fetch(`${API}/api/admin/products/${p.id}`, { method: "PUT", headers: authHeaders(token), body: JSON.stringify({ hidden: !p.hidden }) });
    load();
  };
  const sell = (p) => p.discountPrice || p.price || 0;
  const collOptions = [...new Set(COLLECTIONS.map((c) => c.collection || c.category))];
  const filtered = items.filter((p) => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (fColl !== "all" && (p.collection || p.category) !== fColl) return false;
    const s = sell(p);
    if (fPrice === "u2k" && !(s > 0 && s < 2000)) return false;
    if (fPrice === "2k6k" && !(s >= 2000 && s < 6000)) return false;
    if (fPrice === "6k12k" && !(s >= 6000 && s < 12000)) return false;
    if (fPrice === "a12k" && !(s >= 12000)) return false;
    if (fFlag === "premium" && !p.premium) return false;
    if (fFlag === "featured" && !p.featured) return false;
    if (fFlag === "hidden" && !p.hidden) return false;
    if (fFlag === "visible" && p.hidden) return false;
    return true;
  });
  if (loading) return <p className="admin-hint">Loading…</p>;
  return (
    <div className="admin-card">
      <h3>Products ({filtered.length})</h3>
      <div className="admin-filters">
        <label className="admin-label">Search
          <input className="admin-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name se khojein…" data-testid="admin-search-input" />
        </label>
        <label className="admin-label">Collection
          <select className="admin-input" value={fColl} onChange={(e) => setFColl(e.target.value)} data-testid="admin-filter-collection">
            <option value="all">Sabhi</option>
            {collOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="admin-label">Price range
          <select className="admin-input" value={fPrice} onChange={(e) => setFPrice(e.target.value)} data-testid="admin-filter-price">
            <option value="all">Sabhi</option>
            <option value="u2k">Under ₹2000</option>
            <option value="2k6k">₹2000–₹6000</option>
            <option value="6k12k">₹6000–₹12000</option>
            <option value="a12k">Above ₹12000</option>
          </select>
        </label>
        <label className="admin-label">Type
          <select className="admin-input" value={fFlag} onChange={(e) => setFFlag(e.target.value)} data-testid="admin-filter-flag">
            <option value="all">Sabhi</option>
            <option value="visible">Visible</option>
            <option value="premium">Premium</option>
            <option value="featured">Featured</option>
            <option value="hidden">Hidden</option>
          </select>
        </label>
      </div>
      {!filtered.length && <p className="admin-hint" data-testid="admin-empty-products">Is filter mein koi product nahi mila.</p>}
      <div className="admin-list" data-testid="admin-product-list">
        {filtered.map((p) => (
          <div className="admin-row" key={p.id} data-testid={`admin-product-row-${p.id}`}>
            <img src={p.mainImage} alt="" />
            <div>
              <strong>{p.name}</strong>
              <small>{p.collection || p.category}{p.price ? ` · ₹${p.price.toLocaleString("en-IN")}` : ""}</small>
              <small className="admin-badges">
                {p.premium ? <span className="admin-badge">Premium #{p.premium}</span> : null}
                {p.featured ? <span className="admin-badge">Featured</span> : null}
                {p.hidden ? <span className="admin-badge admin-badge-warn">Hidden</span> : null}
              </small>
            </div>
            <button onClick={() => toggleHide(p)} aria-label={p.hidden ? "Show product" : "Hide product"} data-testid={`admin-hide-${p.id}`}>{p.hidden ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            <button onClick={() => onEdit(p)} aria-label="Edit" data-testid={`admin-edit-${p.id}`}><Pencil size={16} /></button>
            <button onClick={() => del(p)} aria-label="Delete" className="admin-danger" data-testid={`admin-delete-${p.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReelsTab({ token, refreshKey, onChanged }) {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const load = async () => {
    try {
      const res = await fetch(`${API}/api/admin/reels`, { headers: authHeaders(token, false) });
      setItems(await res.json());
    } catch { setItems([]); }
  };
  useEffect(() => { load(); }, [refreshKey]);
  const add = async () => {
    if (!file) { setStatus("Video chunein"); return; }
    setBusy(true); setStatus("Video upload ho rahi hai… (badi file mein time lag sakta hai)");
    try {
      const fd = new FormData();
      fd.append("files", file);
      const up = await fetch(`${API}/api/admin/upload`, { method: "POST", headers: authHeaders(token, false), body: fd });
      const upData = await up.json();
      if (!up.ok) throw new Error(upData.detail || "Upload failed");
      const res = await fetch(`${API}/api/admin/reels`, { method: "POST", headers: authHeaders(token), body: JSON.stringify({ url: upData.urls[0] }) });
      if (!res.ok) throw new Error("Save failed");
      setStatus("Reel live ho gayi ✓"); setFile(null); load(); onChanged();
    } catch (err) { setStatus(err.message); }
    setBusy(false);
  };
  const del = async (r) => {
    if (!window.confirm("Yeh reel delete karein?")) return;
    await fetch(`${API}/api/admin/reels/${r.id}`, { method: "DELETE", headers: authHeaders(token, false) });
    load(); onChanged();
  };
  return (
    <div className="admin-card">
      <h3>Client reels</h3>
      <p className="admin-hint">Website par 3 reels pehle se hain. Yahan se nayi reel add karein (vertical video best hai).</p>
      <label className="admin-label">Reel video (mp4)
        <input className="admin-input" type="file" accept="video/*" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="admin-reel-input" />
      </label>
      {status && <p className="admin-status" data-testid="admin-reel-status">{status}</p>}
      <button className="button button-primary admin-btn" onClick={add} disabled={busy} data-testid="admin-reel-add-button">{busy ? "Uploading…" : "Add reel"}</button>
      <div className="admin-list">
        {items.map((r) => (
          <div className="admin-row" key={r.id}>
            <video src={r.url} muted playsInline preload="metadata" />
            <div><strong>Admin reel</strong><small>Added by you</small></div>
            <button onClick={() => del(r)} aria-label="Delete reel" className="admin-danger" data-testid={`admin-reel-delete-${r.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CoversTab({ token }) {
  const [covers, setCovers] = useState({});
  const [status, setStatus] = useState("");
  const [busyName, setBusyName] = useState("");
  const load = async () => {
    try {
      const res = await fetch(`${API}/api/collection-covers`);
      const data = await res.json();
      setCovers(data || {});
    } catch { /* default covers dikhengi */ }
  };
  useEffect(() => { load(); }, []);
  const upload = async (name, file) => {
    if (!file) return;
    setBusyName(name);
    setStatus(`"${name}" ki cover photo upload ho rahi hai…`);
    try {
      const fd = new FormData();
      fd.append("files", file);
      const up = await fetch(`${API}/api/admin/upload`, { method: "POST", headers: authHeaders(token, false), body: fd });
      const upData = await up.json();
      if (!up.ok) throw new Error(upData.detail || "Upload failed");
      const res = await fetch(`${API}/api/admin/collection-covers`, { method: "PUT", headers: authHeaders(token), body: JSON.stringify({ name, url: upData.urls[0] }) });
      if (!res.ok) throw new Error("Cover save nahi hui");
      setStatus(`"${name}" ki cover photo badal gayi ✓ Website par turant dikhegi.`);
      await load();
    } catch (err) { setStatus(err.message); }
    setBusyName("");
  };
  return (
    <div className="admin-card">
      <h3>Collection cover photos</h3>
      <p className="admin-hint">Homepage ke har collection card ki photo yahan se badlein. Photo upload karte hi website par turant nayi photo dikhegi.</p>
      {status && <p className="admin-status" data-testid="admin-cover-status">{status}</p>}
      <div className="admin-list" data-testid="admin-covers-list">
        {styleCategories.map((c, i) => (
          <div className="admin-row" key={c.name} data-testid={`admin-cover-row-${i}`}>
            <img src={covers[c.name] || c.image} alt={c.name} />
            <div><strong>{c.name}</strong><small>{covers[c.name] ? "Custom cover lagi hui hai" : "Default cover"}</small></div>
            <label className="admin-cover-btn" data-testid={`admin-cover-upload-${i}`}>
              {busyName === c.name ? "…" : <Image size={16} />}
              <input type="file" accept="image/*" hidden onChange={(e) => { upload(c.name, e.target.files && e.target.files[0]); e.target.value = ""; }} />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

function BannerTab({ token }) {
  const [enabled, setEnabled] = useState(false);
  const [text, setText] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/announcement`);
        const data = await res.json();
        setEnabled(!!data.enabled);
        setText(data.text || "");
      } catch { /* defaults rahenge */ }
    })();
  }, []);
  const save = async () => {
    if (enabled && !text.trim()) { setStatus("Banner ON hai — pehle text likhein, ya banner OFF karke save karein."); return; }
    setBusy(true); setStatus("");
    try {
      const res = await fetch(`${API}/api/admin/announcement`, { method: "PUT", headers: authHeaders(token), body: JSON.stringify({ enabled, text: text.trim() }) });
      if (!res.ok) throw new Error("Save nahi hua");
      setStatus(enabled ? "Banner live ho gaya ✓ Homepage ke sabse upar dikhega." : "Banner band ho gaya ✓ Homepage se hat gaya.");
    } catch (err) { setStatus(err.message); }
    setBusy(false);
  };
  return (
    <div className="admin-card">
      <h3>Festive banner</h3>
      <p className="admin-hint">Homepage ke sabse upar ek patli announcement strip dikhegi — festive offer ya nayi collection ke liye. Toggle ON karein, text likhein, save karein. Visitor ek baar band kar de toh us session mein dobara nahi dikhega.</p>
      <label className="admin-toggle" data-testid="admin-banner-toggle">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span className="admin-toggle-track"><span className="admin-toggle-thumb" /></span>
        <strong>{enabled ? "Banner ON" : "Banner OFF"}</strong>
      </label>
      <label className="admin-label">Banner text
        <input className="admin-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Diwali Offer — har saree par 10% OFF" data-testid="admin-banner-text" />
      </label>
      {text.trim() && <p className="admin-hint">Preview: <span className="admin-banner-preview">{text}</span></p>}
      {status && <p className="admin-status" data-testid="admin-banner-status">{status}</p>}
      <button className="button button-primary admin-btn" onClick={save} disabled={busy} data-testid="admin-banner-save">{busy ? "Saving…" : "Save banner"}</button>
    </div>
  );
}

function ReviewsTab({ token, refreshKey, onChanged }) {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [via, setVia] = useState("Instagram");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const load = async () => {
    try {
      const res = await fetch(`${API}/api/reviews`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); }, [refreshKey]);
  const add = async () => {
    if (!name.trim() || !text.trim()) { setStatus("Customer ka naam aur review dono likhein"); return; }
    setBusy(true); setStatus("");
    try {
      const res = await fetch(`${API}/api/admin/reviews`, { method: "POST", headers: authHeaders(token), body: JSON.stringify({ name: name.trim(), text: text.trim(), via }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save nahi hua");
      setStatus("Review live ho gaya ✓ Homepage ke 'Our Balaji family' section mein dikhega.");
      setName(""); setText("");
      load(); onChanged();
    } catch (err) { setStatus(err.message); }
    setBusy(false);
  };
  const del = async (r) => {
    if (!window.confirm(`"${r.name}" ka review delete karein? Website se turant hat jayega.`)) return;
    await fetch(`${API}/api/admin/reviews/${r.id}`, { method: "DELETE", headers: authHeaders(token, false) });
    load(); onChanged();
  };
  return (
    <div className="admin-card">
      <h3>Customer reviews</h3>
      <p className="admin-hint">5 reviews pehle se website par hain. Yahan add kiye gaye reviews homepage par unke saath dikhenge.</p>
      <label className="admin-label">Customer name
        <input className="admin-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Neha Jain" data-testid="admin-review-name" />
      </label>
      <label className="admin-label">Review
        <textarea className="admin-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Customer ne kya likha…" data-testid="admin-review-text" />
      </label>
      <label className="admin-label">Source
        <select className="admin-input" value={via} onChange={(e) => setVia(e.target.value)} data-testid="admin-review-via">
          {["Instagram", "Facebook", "WhatsApp", "In-store"].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </label>
      {status && <p className="admin-status" data-testid="admin-review-status">{status}</p>}
      <button className="button button-primary admin-btn" onClick={add} disabled={busy} data-testid="admin-review-add-button">{busy ? "Please wait…" : "Add review"}</button>
      <div className="admin-list" data-testid="admin-reviews-list">
        {items.map((r) => (
          <div className="admin-row" key={r.id} data-testid={`admin-review-row-${r.id}`}>
            <div><strong>{r.name} <small>· via {r.via}</small></strong><small>{r.text.length > 90 ? r.text.slice(0, 90) + "…" : r.text}</small></div>
            <button onClick={() => del(r)} aria-label="Delete review" className="admin-danger" data-testid={`admin-review-delete-${r.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DashboardTab({ token, refreshKey, go }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/products`, { headers: authHeaders(token, false) });
        const data = await res.json();
        setItems(Array.isArray(data) ? data : []);
      } catch { setItems([]); }
    })();
  }, [refreshKey]);
  const sell = (p) => p.discountPrice || p.price || 0;
  const stats = [
    { label: "Total products", value: items.length, testid: "admin-stat-total" },
    { label: "Premium sarees", value: items.filter((p) => p.premium).length, testid: "admin-stat-premium" },
    { label: "Under ₹2000", value: items.filter((p) => (sell(p) > 0 && sell(p) < 2000) || p.collection === "Under ₹2000").length, testid: "admin-stat-u2k" },
    { label: "Featured", value: items.filter((p) => p.featured).length, testid: "admin-stat-featured" },
    { label: "Hidden", value: items.filter((p) => p.hidden).length, testid: "admin-stat-hidden" },
  ];
  const byCat = ["Sarees", "Lehengas", "Suits"].map((c) => ({ c, n: items.filter((p) => p.category === c).length }));
  return (
    <div className="admin-card" data-testid="admin-dashboard">
      <h3>Dashboard</h3>
      <p className="admin-hint">Aapki dukaan ka ek jhalak — sab kuch live website se juda hua hai.</p>
      <div className="admin-stats">
        {stats.map((s) => <div className="admin-stat" key={s.label} data-testid={s.testid}><strong>{s.value}</strong><span>{s.label}</span></div>)}
      </div>
      <div className="admin-stats">
        {byCat.map((x) => <div className="admin-stat" key={x.c} data-testid={`admin-stat-cat-${x.c.toLowerCase()}`}><strong>{x.n}</strong><span>{x.c}</span></div>)}
      </div>
      <div className="admin-quick">
        <button className="button button-primary admin-btn" onClick={() => go("add")} data-testid="admin-quick-add">Add product</button>
        <button className="button button-outline admin-btn" onClick={() => go("manage")} data-testid="admin-quick-manage">Manage products</button>
        <button className="button button-outline admin-btn" onClick={() => go("manage", { flag: "premium" })} data-testid="admin-quick-premium">Manage premium sarees</button>
        <button className="button button-outline admin-btn" onClick={() => go("manage", { coll: "Under ₹2000" })} data-testid="admin-quick-u2k">Manage Under ₹2000</button>
        <button className="button button-outline admin-btn" onClick={() => go("covers")} data-testid="admin-quick-categories">Manage categories</button>
      </div>
    </div>
  );
}

function SettingsTab({ token }) {
  const [cod, setCod] = useState(false);
  const [status, setStatus] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/settings`);
        const data = await res.json();
        setCod(!!data.cod);
      } catch { /* default off */ }
    })();
  }, []);
  const saveCod = async (val) => {
    setCod(val);
    try {
      const res = await fetch(`${API}/api/admin/settings`, { method: "PUT", headers: authHeaders(token), body: JSON.stringify({ cod: val }) });
      if (!res.ok) throw new Error("Save nahi hua");
      setStatus(val ? "Cash on Delivery ON ✓ Cart page par customer ko COD option milega." : "Cash on Delivery OFF ✓");
    } catch (err) { setStatus(err.message); setCod(!val); }
  };
  return (
    <>
      <div className="admin-card">
        <h3>Store settings</h3>
        <label className="admin-toggle" data-testid="admin-cod-toggle">
          <input type="checkbox" checked={cod} onChange={(e) => saveCod(e.target.checked)} />
          <span className="admin-toggle-track"><span className="admin-toggle-thumb" /></span>
          <strong>{cod ? "Cash on Delivery ON" : "Cash on Delivery OFF"}</strong>
        </label>
        <p className="admin-hint">ON karne par cart page par "Cash on Delivery" option dikhega aur WhatsApp order mein "Payment Method: Cash on Delivery" likha jayega. Default: OFF (aapki existing policy).</p>
        {status && <p className="admin-status" data-testid="admin-cod-status">{status}</p>}
      </div>
      <PasswordTab token={token} />
    </>
  );
}

function PasswordTab({ token }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [status, setStatus] = useState("");
  const change = async () => {
    const res = await fetch(`${API}/api/admin/password`, { method: "POST", headers: authHeaders(token), body: JSON.stringify({ current, new: next }) });
    const data = await res.json();
    setStatus(res.ok ? "Password badal gaya ✓" : (data.detail || "Failed"));
    if (res.ok) { setCurrent(""); setNext(""); }
  };
  return (
    <div className="admin-card">
      <h3>Change password</h3>
      <label className="admin-label">Current password<input className="admin-input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="admin-current-password" /></label>
      <label className="admin-label">New password (min 6 characters)<input className="admin-input" type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="admin-new-password" /></label>
      {status && <p className="admin-status" data-testid="admin-password-status">{status}</p>}
      <button className="button button-primary admin-btn" onClick={change} data-testid="admin-password-change-button">Change password</button>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("sbs_admin") || "");
  const [tab, setTab] = useState("dash");
  const [editing, setEditing] = useState(null);
  const [listPreset, setListPreset] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);
  const login = (t) => { localStorage.setItem("sbs_admin", t); setToken(t); };
  const logout = () => { localStorage.removeItem("sbs_admin"); setToken(""); };
  const go = (t, preset = null) => { setListPreset(preset); setTab(t); if (t === "add") setEditing(null); };

  if (!token) return <main className="admin-page"><LoginForm onLogin={login} /></main>;
  return (
    <main className="admin-page" data-testid="admin-panel">
      <div className="admin-top">
        <Link to="/" className="text-link"><ArrowLeft size={14} /> Website</Link>
        <strong>Admin Panel</strong>
        <button className="text-link admin-logout" onClick={logout} data-testid="admin-logout-button"><LogOut size={14} /> Logout</button>
      </div>
      <div className="admin-tabs">
        {[["dash", "Dashboard"], ["add", editing ? "Edit product" : "Add product"], ["manage", "Products"], ["covers", "Categories"], ["banner", "Banner"], ["reviews", "Reviews"], ["reels", "Reels"], ["settings", "Settings"]].map(([k, label]) => (
          <button key={k} className={tab === k ? "admin-tab active" : "admin-tab"} onClick={() => go(k)} data-testid={`admin-tab-${k}`}>{label}</button>
        ))}
      </div>
      {tab === "dash" && <DashboardTab token={token} refreshKey={refreshKey} go={go} />}
      {tab === "add" && <ProductForm token={token} editing={editing} onDone={() => { setEditing(null); bump(); if (!editing) setTab("manage"); }} onCancel={() => setEditing(null)} />}
      {tab === "manage" && <ProductList token={token} refreshKey={refreshKey} preset={listPreset} onEdit={(p) => { setEditing(p); setTab("add"); }} />}
      {tab === "covers" && <CoversTab token={token} />}
      {tab === "banner" && <BannerTab token={token} />}
      {tab === "reviews" && <ReviewsTab token={token} refreshKey={refreshKey} onChanged={bump} />}
      {tab === "reels" && <ReelsTab token={token} refreshKey={refreshKey} onChanged={bump} />}
      {tab === "settings" && <SettingsTab token={token} />}
    </main>
  );
}
